import { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
} from "firebase/firestore";
import { db, ENABLE_MOCK_FALLBACK } from "@/lib/firebase";
import * as mock from "@/data/mockData";

// --------------------------------------------------------------------------
// Generic helpers
// --------------------------------------------------------------------------

function useCollection(collectionName, { qFn, fallback, normalize, postProcess } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const ref = collection(db, collectionName);
        const q = qFn ? qFn(ref) : ref;
        const snap = await getDocs(q);
        if (cancelled) return;
        if (snap.empty && ENABLE_MOCK_FALLBACK && fallback) {
          const result = normalize ? fallback.map(normalize) : fallback;
          setData(postProcess ? postProcess(result) : result);
        } else {
          const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const result = normalize ? rows.map(normalize) : rows;
          setData(postProcess ? postProcess(result) : result);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        if (ENABLE_MOCK_FALLBACK && fallback) {
          const result = normalize ? fallback.map(normalize) : fallback;
          setData(postProcess ? postProcess(result) : result);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName]);

  return { data, loading, error };
}

function useDocument(path, fallback) {
  const [data, setData] = useState(ENABLE_MOCK_FALLBACK ? (fallback ?? null) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const snap = await getDoc(doc(db, path));
        if (cancelled) return;
        if (snap.exists()) {
          setData({ id: snap.id, ...snap.data() });
        } else if (ENABLE_MOCK_FALLBACK && fallback) {
          setData(fallback);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        if (ENABLE_MOCK_FALLBACK && fallback) setData(fallback);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return { data, loading, error };
}

// --------------------------------------------------------------------------
// Normalizers — keep page components agnostic to Firestore field renames.
// --------------------------------------------------------------------------

const normalizeListing = (l) => ({
  id: l.id,
  title: l.title,
  description: l.description,
  price: l.price,
  bedrooms: l.bedrooms,
  bathrooms: l.bathrooms,
  // legacy field names used by the card components
  type: l.type || l.category,
  location: l.location || l.district,
  size: l.size || l.sizeSqft,
  image: l.image || (Array.isArray(l.images) ? l.images[0] : ""),
  images: Array.isArray(l.images) ? l.images : l.image ? [l.image] : [],
  externalLink: l.externalLink || "",
  videoLink: l.videoLink || "",
  featured: !!l.featured,
  status: l.status || "Active",
  order: typeof l.order === "number" ? l.order : 10,
});

const composeLegacyStory = (c) => {
  // Older case studies stored 3 separate fields; fold them into one story block.
  const parts = [];
  if (c.challenge) parts.push(`<p><strong>Challenge:</strong> ${c.challenge}</p>`);
  if (c.strategy) parts.push(`<p><strong>Strategy:</strong> ${c.strategy}</p>`);
  if (c.outcome) parts.push(`<p><strong>Outcome:</strong> ${c.outcome}</p>`);
  return parts.join("\n");
};

const normalizeCaseStudy = (c) => ({
  id: c.id,
  // legacy mock used `headline` + `metric` + `image`
  headline: c.headline || c.title,
  title: c.title || c.headline,
  metric: c.metric || c.highlight || "",
  highlight: c.highlight || c.metric || "",
  propertyType: c.propertyType || "",
  // single rich-text story (new); falls back to composing the legacy 3 fields
  story: c.story || composeLegacyStory(c),
  challenge: c.challenge || "",
  strategy: c.strategy || "",
  outcome: c.outcome || "",
  image: c.image || (Array.isArray(c.images) ? c.images[0] : ""),
  images: Array.isArray(c.images) ? c.images : c.image ? [c.image] : [],
  order: typeof c.order === "number" ? c.order : 10,
  status: c.status || "Active",
});

const normalizeTestimonial = (t, i) => ({
  id: t.id,
  name: t.name || t.homeownerName,
  role: t.role || t.homeownerDesc || "",
  title: t.title || t.testimonialTitle || "",
  quote: t.quote || t.testimonialDesc,
  avatar: t.avatar || t.image,
  photo: t.photo || t.image,
  homeownerName: t.homeownerName || t.name,
  homeownerDesc: t.homeownerDesc || t.role || "",
  testimonialDesc: t.testimonialDesc || t.quote,
  testimonialDate: t.testimonialDate || "",
  image: t.image || t.avatar || t.photo,
  displayOrder: typeof t.displayOrder === "number" ? t.displayOrder : i + 1,
  featured: typeof t.featured === "boolean" ? t.featured : true,
  status: t.status || "Active",
});

const normalizeAward = (a) => ({
  id: a.id,
  title: a.title || "",
  year: a.year || a.description || "",
  description: a.description || a.year || "",
  uploadedAt: a.uploadedAt || a.awardDate || "",
  awardDate: a.awardDate || a.uploadedAt || "",
  image: a.image,
  status: a.status || "Active",
});

const normalizeBlogPost = (b) => ({
  id: b.id,
  slug: b.slug,
  title: b.title,
  category: b.category,
  categoryId: b.categoryId,
  date: b.date || b.publishDate || "",
  publishDate: b.publishDate || b.date || "",
  createdAt: b.createdAt || null,
  image: b.image || b.featuredImage || "",
  featuredImage: b.featuredImage || b.image || "",
  excerpt: b.excerpt || "",
  content: b.content || b.excerpt || "",
  metaTitle: b.metaTitle || b.title,
  metaDesc: b.metaDesc || b.excerpt || "",
  keywords: Array.isArray(b.keywords) ? b.keywords : [],
  status: b.status || "Published",
});

const normalizeBlogCategory = (c) =>
  typeof c === "string"
    ? {
        id: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        name: c,
        slug: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      }
    : {
        id: c.id,
        name: c.name,
        slug: c.slug || c.id,
      };

// --------------------------------------------------------------------------
// Agent / brand / site config
// --------------------------------------------------------------------------

const agentFallback = {
  ...mock.brand,
  portraits: mock.portraits,
  trustHighlights: mock.trustHighlights,
  socialLinks: mock.socialLinks,
  advisoryAreas: mock.advisoryAreas,
  clarityFramework: mock.clarityFramework,
  consultingServices: mock.consultingServices,
  marketingSolutions: mock.marketingSolutions,
  frameworkArtwork: mock.frameworkArtwork,
  externalLinks: mock.externalLinks,
};

export function useAgent() {
  return useDocument("agent/profile", agentFallback);
}

const siteFallback = {
  pageMeta: mock.pageMeta,
  listingPortals: mock.listingPortals,
  blogCategories: mock.blogCategories,
};
export function useSiteSettings() {
  return useDocument("siteSettings/global", siteFallback);
}

// --------------------------------------------------------------------------
// Content collections
// --------------------------------------------------------------------------

export function useListings({ onlyActive = true, featuredOnly = false } = {}) {
  return useCollection("listings", {
    qFn: (ref) => {
      const clauses = [];
      if (onlyActive) clauses.push(where("status", "==", "Active"));
      if (featuredOnly) clauses.push(where("featured", "==", true));
      clauses.push(orderBy("order", "asc"));
      return query(ref, ...clauses);
    },
    fallback: mock.listings,
    normalize: normalizeListing,
  });
}

export function useCaseStudies() {
  return useCollection("caseStudies", {
    qFn: (ref) =>
      query(ref, where("status", "==", "Active"), orderBy("order", "asc")),
    fallback: mock.caseStudies,
    normalize: normalizeCaseStudy,
  });
}

export function useTestimonials({ featuredOnly = false } = {}) {
  return useCollection("testimonials", {
    qFn: (ref) => {
      const clauses = [where("status", "==", "Active")];
      if (featuredOnly) clauses.push(where("featured", "==", true));
      return query(ref, ...clauses);
    },
    fallback: mock.testimonials,
    normalize: (t, i) => {
      const normalized = normalizeTestimonial(t, i);
      return normalized;
    },
    postProcess: (items) =>
      [...items].sort((a, b) => {
        if (a.testimonialDate && b.testimonialDate)
          return b.testimonialDate.localeCompare(a.testimonialDate);
        if (a.testimonialDate) return -1;
        if (b.testimonialDate) return 1;
        return a.displayOrder - b.displayOrder;
      }),
  });
}

export function useAwards() {
  return useCollection("awards", {
    qFn: (ref) =>
      query(
        ref,
        where("status", "==", "Active"),
        orderBy("awardDate", "desc")
      ),
    fallback: mock.awards,
    normalize: normalizeAward,
  });
}

export function useBlogCategories() {
  return useCollection("blogCategories", {
    fallback: mock.blogCategories,
    normalize: normalizeBlogCategory,
  });
}

export function useBlogPosts({ categoryId, max } = {}) {
  return useCollection("blogPosts", {
    qFn: (ref) => {
      const clauses = [where("status", "==", "Published")];
      if (categoryId) clauses.push(where("categoryId", "==", categoryId));
      clauses.push(orderBy("createdAt", "desc"));
      if (max) clauses.push(limit(max));
      return query(ref, ...clauses);
    },
    fallback: mock.blogPosts,
    normalize: normalizeBlogPost,
    postProcess: (items) =>
      [...items].sort((a, b) => {
        const ta = a.createdAt?.seconds ?? 0;
        const tb = b.createdAt?.seconds ?? 0;
        return tb - ta;
      }),
  });
}

export function useBlogPostBySlug(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const q = query(
          collection(db, "blogPosts"),
          where("slug", "==", slug),
          where("status", "==", "Published"),
          limit(1)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        if (!snap.empty) {
          const d = snap.docs[0];
          setData(normalizeBlogPost({ id: d.id, ...d.data() }));
        } else if (ENABLE_MOCK_FALLBACK) {
          const fb = (mock.blogPosts || []).find((p) => p.slug === slug);
          if (fb) setData(normalizeBlogPost(fb));
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        if (ENABLE_MOCK_FALLBACK) {
          const fb = (mock.blogPosts || []).find((p) => p.slug === slug);
          if (fb) setData(normalizeBlogPost(fb));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { data, loading, error };
}

export function useListingById(id) {
  const fb = (mock.listings || []).find((l) => l.id === id);
  const { data, loading, error } = useDocument(
    `listings/${id}`,
    fb ? normalizeListing(fb) : null
  );
  return { data: data ? normalizeListing(data) : null, loading, error };
}

export function useCaseStudyById(id) {
  const fb = (mock.caseStudies || []).find((c) => c.id === id);
  const { data, loading, error } = useDocument(
    `caseStudies/${id}`,
    fb ? normalizeCaseStudy(fb) : null
  );
  return { data: data ? normalizeCaseStudy(data) : null, loading, error };
}

// --------------------------------------------------------------------------
// SEO settings (per-page)
// --------------------------------------------------------------------------

export function useSeo(pageKey) {
  const fb = mock.pageMeta?.[pageKey];
  return useDocument(
    `seoSettings/${pageKey}`,
    fb
      ? {
          metaTitle: fb.title,
          metaDesc: fb.description,
          keywords: fb.keywords || "",
        }
      : null
  );
}
